/**
    * @description      : 
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBorderAll,
  faUser,
  faPenFancy,
  faMoneyBill,
  faTableList,
  faTag,
  faYenSign,
  faHeadphonesAlt,
  faVideo,
  faAd,
  faChevronUp,
  faChevronDown,
  faBuilding,
  faTeletype,
  faBars,
  faBed,
  faPlaceOfWorship,
  faSnowflake,
  faSquareParking,
  faHome,
  faCalendarDays,
  faClock,
  faKey,
  faCreditCard,
  faExternalLinkAlt,
  faWallet,
  faFileInvoiceDollar,
  faReceipt,
  faFilePen
} from "@fortawesome/free-solid-svg-icons";
import {
  faHeart,
  faMessage,
} from "@fortawesome/free-regular-svg-icons";

import "./style.css";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { useEffect, useState } from "react";
import { useGet, usePost } from "../../../Api";
import CustomModal from "../../CustomModal";
import CustomButton from "../../CustomButton";

export const Sidebar = (props) => {
  const [isRead, setIsRead] = useState(false);
  const [userData, setUserData] = useState(null);
  const [stripeAccountData, setStripeAccountData] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [stripeOnboardingUrl, setStripeOnboardingUrl] = useState('');

  const { ApiData: StripeAccountData, loading: StripeAccountLoading, error: StripeAccountError, post: GetStripeAccount } = usePost(`/stripe/refresh-account`);
  const { ApiData: CreateStripeAccountData, loading: CreateStripeAccountLoading, error: CreateStripeAccountError, post: CreateStripeAccount } = usePost(`/stripe/create-account`);

  const [openMenus, setOpenMenus] = useState({
    listing: false,
    // Add more keys if you have more parent menus
  });

  useEffect(() => {
    // Get user data from localStorage
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      setUserData(JSON.parse(userInfo));
    }
  }, []);

  // Check Stripe account status for hosts
  useEffect(() => {
    if (userData && userData.role === "2") {
      GetStripeAccount({}); // Send empty object for POST request
    }
  }, [userData]);

  useEffect(() => {
    if (StripeAccountData) {
      setStripeAccountData(StripeAccountData);
    }
  }, [StripeAccountData]);

  // Handle Stripe account creation
  useEffect(() => {
    if (CreateStripeAccountData && CreateStripeAccountData.success) {
      setStripeOnboardingUrl(CreateStripeAccountData.data.onboarding_url);
      setShowStripeModal(true);
      // Refresh the Stripe account status after creation
      setTimeout(() => {
        GetStripeAccount({});
      }, 1000);
    }
  }, [CreateStripeAccountData]);

  // Refresh Stripe account status when modal is closed
  const handleCloseModal = () => {
    setShowStripeModal(false);
    // Refresh status after modal closes
    if (userData && userData.role === "2") {
      GetStripeAccount({});
    }
  };

  const handleCreateStripeAccount = () => {
    if (userData) {
      const payload = {
        email: userData.email,
        business_name: userData.name,
        country: "US", // You can make this dynamic if needed
        type: "express"
      };
      CreateStripeAccount(payload);
    }
  };

  const handleNavigateToStripe = () => {
    if (stripeOnboardingUrl) {
      window.open(stripeOnboardingUrl, '_blank');
      setShowStripeModal(false);
    }
  };

  const toggleMenu = (menuKey) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const isActive = (path) => location.pathname === path;

  const location = useLocation();
  
  // Check if user is a host and needs Stripe setup
  const isHost = userData && userData.role === "2";
  const isRenter = userData && userData.role === "3";
  const needsStripeSetup = isHost && stripeAccountData && stripeAccountData.data && stripeAccountData.data.status === "not_created";

  // Host Menu Items
  const hostMenuItems = [
    {
      path: "/dashboard",
      icon: faBorderAll,
      text: "Dashboard",
      active: location.pathname.includes('/dashboard')
    },
    {
      path: "/reservation",
      icon: faUsers,
      text: "Reservation",
      active: location.pathname.includes('reservation')
    },
    {
      path: "/listing-management",
      icon: faBuilding,
      text: "Listings",
      active: location.pathname.includes('listing-management')
    },
    {
      path: "/draft-listings",
      icon: faFilePen,
      text: "Draft Listings",
      active: location.pathname.includes('draft-listings')
    },
    {
      path: "/wallet",
      icon: faWallet,
      text: "Wallet",
      active: location.pathname.includes('wallet')
    }
  ];

  // Renter Menu Items
  const renterMenuItems = [
    {
      path: "/dashboard",
      icon: faBorderAll,
      text: "Dashboard",
      active: location.pathname.includes('/dashboard')
    },
    {
      path: "/reservation",
      icon: faUsers,
      text: "Reservation",
      active: location.pathname.includes('reservation')
    },
    {
      path: "/user-invoice",
      icon: faFileInvoiceDollar,
      text: "Invoice",
      active: location.pathname.includes('user-invoice')
    },
  ];

  // Get menu items based on role
  const getMenuItems = () => {
    if (isHost) {
      return hostMenuItems;
    } else if (isRenter) {
      return renterMenuItems;
    }
    return []; // Default empty array if role is not recognized
  };

  const menuItems = getMenuItems();

  return (
    <>
      <div className={`sidebar ${props.sideClass}`} id="sidebar">
        <ul className="list-unstyled">
          {/* Role-based menu items */}
          {menuItems.map((item, index) => (
            <li key={index} className="sidebar-li">
              <Link className={`sideLink ${item.active ? 'active' : ''}`} to={item.path}>
                <span className="sideIcon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <span className="sideLinkText">{item.text}</span>
              </Link>
            </li>
          ))}

          {/* Stripe Setup Button for Hosts */}
          {needsStripeSetup && (
            <li className="sidebar-li">
              <div 
                className="sideLink stripe-setup-alert" 
                style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', cursor: 'pointer' }}
                onClick={handleCreateStripeAccount}
              >
                <span className="sideIcon">
                  <FontAwesomeIcon icon={faCreditCard} style={{ color: '#856404' }} />
                </span>
                <span className="sideLinkText" style={{ color: '#856404', fontWeight: '600' }}>
                  Setup Stripe Account
                </span>
              </div>
            </li>
          )}

          {/* Profile Section - Available for both roles */}
          <li className="sidebar-li">
            <div
              className={`sideLink`}
              onClick={() => toggleMenu('listing')}
              style={{ cursor: 'pointer' }}
            >
              <span className="sideIcon">
                <FontAwesomeIcon icon={faUser} />
              </span>
              <span className="sideLinkText">Profile</span>
              <span className="submenu-toggle-icon ps-2">
                <FontAwesomeIcon icon={openMenus.listing ? faChevronUp : faChevronDown} />
              </span>
            </div>

            {openMenus.listing && (
              <ul className="subMenu">
                <li className="sidebar-li">
                  <Link
                    className={`sideLink ${location.pathname.includes('profile') ? 'active' : ''}`}
                    to="/profile"
                  >
                    <span className="sideIcon">
                      <FontAwesomeIcon icon={faUser} />
                    </span>
                    <span className="sideLinkText">Edit Profile</span>
                  </Link>
                </li>

                <li className="sidebar-li">
                  <Link
                    className={`sideLink ${location.pathname.includes('change-password') ? 'active' : ''}`}
                    to="/change-password"
                  >
                    <span className="sideIcon">
                      <FontAwesomeIcon icon={faKey} />
                    </span>
                    <span className="sideLinkText">Change Password</span>
                  </Link>
                </li>
              </ul>
            )}
          </li>

        
        </ul>
      </div>

      {/* Stripe Onboarding Modal */}
      <CustomModal 
        show={showStripeModal} 
        close={handleCloseModal} 
        heading="Stripe Account Created Successfully!"
      >
        <div className="text-center p-4">
          <div className="mb-4">
            <div className="stripe-success-icon mb-3">
              <FontAwesomeIcon 
                icon={faCreditCard} 
                style={{ 
                  fontSize: '3rem', 
                  color: '#6772e5',
                  backgroundColor: '#f8f9ff',
                  padding: '1rem',
                  borderRadius: '50%',
                  border: '2px solid #e6e8ff'
                }} 
              />
            </div>
            <h4 className="text-success mb-3">🎉 Account Setup Complete!</h4>
            <p className="text-muted mb-4">
              Your Stripe account has been created successfully. To start receiving payments, 
              you need to complete the onboarding process with Stripe.
            </p>
          </div>
          
          <div className="alert alert-info mb-4" role="alert">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
              <small>
                <strong>Note:</strong> You'll be redirected to Stripe's secure onboarding page in a new tab.
              </small>
            </div>
          </div>
          
          <div className="d-flex gap-3 justify-content-center">
            <CustomButton 
              variant="outline-secondary" 
              text="Cancel" 
              onClick={handleCloseModal}
            />
            <CustomButton 
              variant="primary" 
              text="Complete Onboarding" 
              onClick={handleNavigateToStripe}
              icon={faExternalLinkAlt}
              style={{
                background: 'linear-gradient(135deg, #6772e5 0%, #5a67d8 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(103, 114, 229, 0.3)'
              }}
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};
